export function normalizePayment(raw) {
    if (!raw || typeof raw !== "object") return null;

    const amountPaidSmallest =
        raw.amount_paid != null
            ? Number(raw.amount_paid)
            : raw.amountPaid != null
              ? Number(raw.amountPaid)
              : null;

    const amount =
        raw.amount != null
            ? Number(raw.amount)
            : raw.unit_amount != null
              ? Number(raw.unit_amount) / 100
              : amountPaidSmallest != null
                ? amountPaidSmallest / 100
                : null;

    const receiptUrl =
        raw.receipt_url ??
        raw.receiptUrl ??
        raw.receipt_pdf_url ??
        raw.receiptPdfUrl ??
        null;

    const invoiceUrl = raw.invoice_url ?? raw.invoiceUrl ?? null;

    return {
        payment_id: raw.payment_id ?? raw.paymentId ?? raw.id ?? null,
        user_id: raw.user_id ?? raw.userId ?? null,
        plan_id: raw.plan_id ?? raw.planId ?? null,
        plan_name: raw.plan_name ?? raw.planName ?? raw.plan?.name ?? "Plan",
        amount,
        amount_paid: amountPaidSmallest,
        amount_display: raw.amount_display ?? raw.amountDisplay ?? null,
        currency: raw.currency ?? "inr",
        status: raw.status ?? null,
        payment_status: raw.payment_status ?? raw.paymentStatus ?? null,
        subscription_id: raw.subscription_id ?? raw.subscriptionId ?? null,
        customer_id: raw.customer_id ?? raw.customerId ?? null,
        quota_instance_id: raw.quota_instance_id ?? raw.quotaInstanceId ?? null,
        plan_period_start: raw.plan_period_start ?? raw.planPeriodStart ?? null,
        plan_period_end: raw.plan_period_end ?? raw.planPeriodEnd ?? null,
        created_at: raw.created_at ?? raw.createdAt ?? null,
        updated_at: raw.updated_at ?? raw.updatedAt ?? null,
        paid_at: raw.paid_at ?? raw.paidAt ?? raw.completed_at ?? raw.completedAt ?? null,
        invoice_url: invoiceUrl,
        receipt_url: receiptUrl,
        session_id: raw.session_id ?? raw.sessionId ?? null,
        checkout_url: raw.checkout_url ?? raw.checkoutUrl ?? null,
    };
}

/** Merge nested `payment` / `data` with sibling fields from the API envelope. */
export function unwrapPaymentResponse(data) {
    if (!data || typeof data !== "object") return null;

    const nested = data.payment ?? data.data ?? null;

    if (nested && typeof nested === "object") {
        return normalizePayment({
            ...nested,
            payment_id: nested.payment_id ?? nested.paymentId ?? data.payment_id ?? data.paymentId,
            receipt_url:
                nested.receipt_url ??
                nested.receiptUrl ??
                data.receipt_url ??
                data.receiptUrl,
            invoice_url:
                nested.invoice_url ??
                nested.invoiceUrl ??
                data.invoice_url ??
                data.invoiceUrl,
            amount_display:
                nested.amount_display ??
                nested.amountDisplay ??
                data.amount_display ??
                data.amountDisplay,
            amount_paid: nested.amount_paid ?? nested.amountPaid ?? data.amount_paid ?? data.amountPaid,
            plan_name:
                nested.plan_name ??
                nested.planName ??
                data.plan_name ??
                data.planName,
        });
    }

    return normalizePayment(data);
}

export function normalizePaymentsResponse(data) {
    let payments = [];
    let hasCursorPagination = false;
    let hasMore = false;
    let lastDocId = null;

    if (Array.isArray(data)) {
        payments = data;
    } else if (Array.isArray(data?.data)) {
        payments = data.data;
        hasCursorPagination = data.has_more != null || data.last_doc_id != null;
        hasMore = Boolean(data.has_more);
        lastDocId = data.last_doc_id ?? null;
    } else if (Array.isArray(data?.payments)) {
        payments = data.payments;
        hasCursorPagination = data.has_more != null || data.last_doc_id != null;
        hasMore = Boolean(data.has_more);
        lastDocId = data.last_doc_id ?? null;
    }

    return {
        items: payments.map(normalizePayment).filter(Boolean),
        hasCursorPagination,
        hasMore,
        lastDocId,
    };
}

export function formatPaymentAmount(payment) {
    if (!payment) return "—";

    if (payment.amount_display) return payment.amount_display;

    const currency = String(payment.currency || "inr").toUpperCase();
    const amount =
        payment.amount != null
            ? Number(payment.amount)
            : payment.amount_paid != null
              ? Number(payment.amount_paid) / 100
              : null;

    if (amount == null) return "—";

    try {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `₹${amount}`;
    }
}

export function formatPaymentDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

const STATUS_STYLES = {
    completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
    pending: "border-amber-200 bg-amber-50 text-amber-700",
    failed: "border-red-200 bg-red-50 text-red-700",
    canceled: "border-slate-200 bg-slate-100 text-slate-600",
};

export function getPaymentStatusStyle(status) {
    return STATUS_STYLES[status] ?? "border-slate-200 bg-slate-50 text-slate-600";
}
